import { Divider, Snackbar } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import {
  getValueFromLocalStorage,
  setValueInLocalStorage
} from '@openhouse-technologies/utils'
import { isNil, isEmpty } from 'lodash'
import React, { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useCart } from 'react-use-cart'
import '../../styles.css'

import { fetchUser, getProgramFee, getProgramBatches } from 'api'
import ModalForm from 'app/components/ModalForm'
import { ReverifyButton } from 'app/components/shared/components/ReverifyButton'
import { navigationPaths } from 'app/config'
import constants from 'app/constants'
import { confirm, eventAlert } from 'app/helpers'
import {
  batchTypesMap,
  conflictTypes
} from 'app/pages/Class/Inventory/constants'
import { sentryLogger } from 'sentry'

import { Actions } from './components/Actions'
import { CourseDetail } from './components/CourseDetail'
import { ParentChildForm } from './components/ParentChildForm'
import { demoSchema } from './formConfig'
import * as Styles from './styles'

import { SeparationContainer } from '../../../styles'
import {
  validateItemForCart,
  validateParentDetails,
  programFlowValidateStudentBatchDetails,
  getCartItemStructure
} from '../helpers'

const AddProgramItemToCartModal = ({
  isModalOpen,
  bookingType,
  selectedProgram,
  onHandleModalClose,
  selectedBatchType,
  selectedCentres
}) => {
  const LinkRedirector = ({ href }) => {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ cursor: 'pointer' }}
      >
        here
      </a>
    )
  }

  const {
    addItem: addItemToCart,
    isEmpty: isCartEmpty,
    items: currentCartItems,
    setCartMetadata,
    metadata: cartMetadata,
    emptyCart
  } = useCart()

  const [formData, setFormData] = useState({})
  const [batchInfo, setBatchInfo] = useState({})
  const [errorSchema, setErrorSchema] = useState({})

  const isParentLocked =
    !isCartEmpty &&
    [batchTypesMap.school, batchTypesMap.care].includes(cartMetadata?.cartType)

  const [selectedParentDetails, setSelectedParentDetails] = useState(
    isParentLocked ? getValueFromLocalStorage('CART_PARENT') : {} || {}
  )

  const [availableBatches, setAvailableBatches] = useState([])
  const [batchDetails, setBatchDetails] = useState(null)
  const [selectedStudentDetails, setSelectedStudentDetails] = useState({})
  const [students, setStudents] = useState([])
  const [inputError, setInputError] = useState({
    parent: null,
    student: null,
    batch: null
  })
  const [courseFormDisabled, setCourseFormDisabled] = React.useState(true)
  // Separate lock for the start-date picker. A class conflict is date-relative:
  // the answer changes the moment a later start is chosen, so the one control
  // that can clear it must stay usable while the rest of the form is locked.
  // Every other refusal (slots full, already enrolled, bad student record) is
  // not fixed by a date, so those keep the picker locked too.
  const [startDateDisabled, setStartDateDisabled] = React.useState(true)
  const [courseFormValid, setCourseFormValid] = React.useState(false)
  const [studentConflictDetails, setStudentConflictDetails] = React.useState({
    isStudentValid: null,
    message: '',
    description: ''
  })

  const [disableAddToCart, setDisableAddtoCart] = React.useState(true)
  const [currentBatchInfo, setCurrentBatchInfo] = React.useState({})
  const [areSlotsAvailable, setAreSlotsAvailable] = React.useState(true)

  const formRef = useRef(null)
  const dispatch = useDispatch()

  // The start date currently chosen for the selected batch, as YYYY-MM-DD.
  // `fetchProgramStartDates` seeds it with the batch's first future session,
  // so it is set well before the operator touches the dropdown.
  const selectedStartDate =
    batchInfo?.[batchDetails?.uuid]?.startDate?.value || null

  const [statusMessage, setStatusMessage] = useState({
    message: '',
    severity: 'info',
    open: false
  })

  const showMessage = (message, severity = 'info') => {
    setStatusMessage({ message, severity, open: true })
  }

  const handleCloseMessage = () => {
    setStatusMessage((prev) => ({ ...prev, open: false }))
  }

  useEffect(() => {
    ;(async () => {
      if (
        !isEmpty(selectedParentDetails) &&
        !isEmpty(selectedStudentDetails) &&
        !isEmpty(batchDetails)
      ) {
        const isParentValid = validateParentDetails(
          selectedParentDetails
        )?.isValid

        showMessage('Checking for batch eligibility...')
        const {
          isValid: isStudentValid,
          message,
          description,
          invalidAttributes: studentInvalidAttributes
        } = await programFlowValidateStudentBatchDetails(
          selectedStudentDetails,
          batchDetails,
          selectedStartDate
        )

        setStudentConflictDetails({
          isStudentValid,
          message,
          description
        })

        // Only enable form if student is valid AND there are slots available
        const parentStudentValid = isParentValid && isStudentValid
        setCourseFormDisabled(!parentStudentValid || !areSlotsAvailable)

        // A conflict against classes the child has already finished by the
        // intended start date is not a real clash. Leave the date picker live
        // so the operator can move the start past them; the check re-runs with
        // that date and unlocks the rest of the form if it clears.
        const onlyBlockerIsClassConflict =
          !isStudentValid &&
          message === conflictTypes.CLASS_CONFLICT &&
          isEmpty(studentInvalidAttributes)
        setStartDateDisabled(
          (!parentStudentValid && !onlyBlockerIsClassConflict) ||
            !isParentValid ||
            !areSlotsAvailable
        )

        if (!isStudentValid && studentInvalidAttributes?.length > 0) {
          setInputError((prev) => ({
            ...prev,
            student: (
              <div>
                Student details ({studentInvalidAttributes.join(', ')}) invalid,
                click{' '}
                <LinkRedirector
                  href={`${window.location.origin}${navigationPaths.student}?uuid=${selectedStudentDetails?.user?.uuid}&editMode=1`}
                />{' '}
                to update
              </div>
            )
          }))
        } else if (!isStudentValid && message) {
          // Keep existing conflict message if no invalid attributes but has other validation message
          setInputError((prev) => ({
            ...prev,
            student: message
          }))
        }
      } else {
        setCurrentBatchInfo({})
        setCourseFormDisabled(true)
        setStartDateDisabled(true)
      }
    })()
  }, [
    selectedParentDetails,
    selectedStudentDetails,
    batchDetails,
    areSlotsAvailable,
    selectedStartDate
  ])

  const handleBatchSelection = (event) => {
    const { value } = event.target
    setBatchDetails(value)
  }

  useEffect(() => {
    const setupBatch = async () => {
      if (
        !batchDetails ||
        isEmpty(selectedStudentDetails) ||
        isEmpty(selectedProgram)
      ) {
        return
      }

      setAreSlotsAvailable(batchDetails.vacancy > 0)

      if (batchDetails.vacancy === 0) {
        setCourseFormDisabled(true)
        setInputError((prev) => ({
          ...prev,
          batch: 'This batch is full. Please select another batch.'
        }))
      } else {
        setInputError((prev) => ({
          ...prev,
          batch: null
        }))
      }
      let feeFound = false
      let annualFee = 0
      let programFee = null

      // Fetch program fee
      try {
        const feesResponse = await getProgramFee({
          queries: {
            centre_uuid: selectedCentres?.value,
            program_uuid: selectedProgram?.uuid,
            status: 'active',
            standards_uuid: selectedStudentDetails?.standard?.uuid,
            start_date: currentBatchInfo?.startDate?.value
          }
        })
        feeFound = feesResponse?.data?.results?.length > 0 ?? false
        annualFee = feesResponse?.data?.results?.[0]?.annual_amount ?? 0
        programFee = feesResponse?.data?.results?.[0]?.uuid
      } catch (e) {
        console.error('Error fetching program Fee')
      }

      // Update batch info with the fetched fee or default to 0
      setBatchInfo((prevState) => ({
        ...prevState,
        [batchDetails.uuid]: {
          ...prevState[batchDetails.uuid],
          price: 0,
          annualFee,
          feeFound,
          programFee
        }
      }))
    }

    setupBatch()
  }, [
    batchDetails,
    selectedStudentDetails,
    selectedProgram,
    selectedCentres,
    currentBatchInfo?.startDate?.value
  ])

  useEffect(() => {
    const fetchBatches = async () => {
      if (!isEmpty(selectedStudentDetails)) {
        try {
          showMessage('Fetching available batches...')
          const response = await getProgramBatches({
            queries: {
              centre_uuid: selectedCentres?.value,
              type: selectedBatchType,
              standards_uuid: selectedStudentDetails?.standard?.uuid,
              program_uuid: selectedProgram?.uuid
            }
          })

          if (response.status === 200) {
            setAvailableBatches(response?.data?.results)
            // Auto-select if only one batch available
            if (response?.data?.results.length === 1) {
              setBatchDetails(response.data.results[0])
            } else if (response?.data?.results.length === 0) {
              showMessage('No batches available', 'warning')
            }
          }
        } catch (error) {
          sentryLogger('Error fetching batches:', error)
          eventAlert('Failed to fetch available batches', constants.ERROR)
        }
      } else {
        setAvailableBatches([])
        setBatchDetails(null)
      }
    }

    if (!isEmpty(selectedStudentDetails) && !isEmpty(selectedProgram))
      fetchBatches()
  }, [selectedStudentDetails, selectedProgram, selectedCentres])

  useEffect(() => {
    if (!selectedProgram) return
    handleSelectedParentChange()
  }, [selectedProgram, selectedParentDetails])

  useEffect(() => {
    setDisableAddtoCart(courseFormDisabled || !courseFormValid)
  }, [courseFormDisabled, courseFormValid])

  const handleSelectedParentChange = () => {
    if (!isEmpty(selectedParentDetails)) {
      setSelectedStudentDetails({})
      setBatchDetails(null)

      if (selectedParentDetails.students.length === 0) {
        eventAlert(
          'No students found! Add a student for the parent.',
          constants.ERROR
        )
      }

      const mappedStudents = selectedParentDetails?.students.map((student) => {
        const isEligible = selectedProgram?.standardsShortCodes?.some(
          (shortCode) => {
            return shortCode === student?.standard?.short_code
          }
        )

        return {
          ...student,
          isDisabled: !isEligible,
          message: null
        }
      })

      setStudents(mappedStudents || [])
      handleValidateParentDetails(selectedParentDetails)
    } else {
      setStudents([])
      setSelectedStudentDetails({})
      setBatchDetails(null)
      setInputError({
        parent: null,
        student: null,
        grade: null
      })
      setDisableAddtoCart(true)
      setCurrentBatchInfo({})
    }
  }

  const handleValidateParentDetails = (selectedParentDetails) => {
    let error = null,
      isValid = false

    if (isEmpty(selectedParentDetails)) {
      error = 'Please Select Parent'
    } else {
      const result = validateParentDetails(selectedParentDetails)

      isValid = result.isValid

      if (!isValid) {
        error = (
          <div>
            Parent details ({result.invalidAttributes.join(', ')}) invalid,
            click{' '}
            <LinkRedirector
              href={`${window.location.origin}${navigationPaths.parent}?uuid=${selectedParentDetails?.uuid}&editMode=1`}
            />{' '}
            to update, <ReverifyButton />
          </div>
        )
      }
    }

    setInputError((state) => ({
      ...state,
      parent: error
    }))

    return isValid
  }

  const handleParentSelection = async (name, value) => {
    setSelectedParentDetails(value || {})
    if (!isNil(value)) {
      await verifyDetails(value?.uuid)
    }
  }

  const handleStudentSelection = (event) => {
    const { value } = event.target
    const selectedStudent = students.find((item) => item.user.uuid === value)
    let studentDetails = selectedStudent || {}
    if (selectedStudent) {
      studentDetails.parent = selectedParentDetails.uuid
    }
    setSelectedStudentDetails(studentDetails)
    setBatchDetails(null) // Reset grade when student changes
    setAvailableBatches([])
    setInputError((state) => ({ ...state, batch: null }))
  }

  const verifyDetails = async (parent_uuid) => {
    try {
      showMessage('Fetching children details...')
      const queries = { uuid: parent_uuid }
      const response = await fetchUser({ queries }, 'parent')

      if (response.results.length === 1) {
        const item = response.results[0]
        setSelectedParentDetails({
          uuid: item.user.uuid,
          value: item.user.uuid,
          name: `${item.user.phone_number} (${item.user.first_name} ${item.user.last_name})`,
          first_name: item.user.first_name,
          last_name: item.user.last_name,
          phone_number: item.user.phone_number,
          state: item.user.state,
          email: item.user.email_address,
          students: item.students,
          coins: item.user.coin_summary?.remaining_coins,
          credits: item.user.credits,
          member_status: item.user.member_status,
          centre_preference: item.centre_preference
        })

        setStudents(item.students || [])
        showMessage('Children details fetched successfully', 'success')
      } else {
        throw new Error('Multiple users found with the same number')
      }
    } catch (error) {
      console.error('Error in verifying user details:', error)
      eventAlert(error.message, constants.ERROR)
    }
  }

  const onAddToCartClick = async () => {
    const userInfo = {
      student: selectedStudentDetails,
      parent: selectedParentDetails
    }

    const cartItem = getCartItemStructure(
      batchDetails,
      batchInfo[batchDetails.uuid],
      userInfo,
      selectedProgram
    )

    // Check if cart has any items and if they are of different type
    const hasConflictingItems =
      !isCartEmpty &&
      cartMetadata?.cartType !== 'school' &&
      cartMetadata?.cartType !== 'care'

    const addItemToCartAndClose = () => {
      const cartValidation = validateItemForCart(currentCartItems, cartItem)
      if (cartValidation.isValid) {
        addItemToCart(cartItem)
        setValueInLocalStorage('CART_PARENT', selectedParentDetails)
        setCartMetadata({ cartType: selectedBatchType })
        showMessage('Item added to cart successfully', 'success')
        onModalClose()
      } else {
        eventAlert(cartValidation?.message, constants.ERROR)
      }
    }

    if (hasConflictingItems) {
      confirm(
        () => {
          emptyCart()
          addItemToCartAndClose()
        },
        `Your cart contains items from a different batch type. Do you want to replace your existing cart with this item?`,
        () => {}, // Do nothing if user cancels
        {
          title: 'replace cart item?',
          icon: '',
          confirmButtonText: 'replace',
          icon: null,
          customClass: {
            confirmButton: 'cart-replace-confirm-button cart-replace-button',
            cancelButton: 'cart-replace-cancel-button cart-replace-button'
          },
          buttonsStyling: false
        }
      )
    } else {
      addItemToCartAndClose()
    }
  }

  const onModalClose = () => {
    setFormData({})
    setBatchInfo({})
    setErrorSchema({})
    setSelectedStudentDetails({})
    setBatchDetails(null)
    setStudents([])
    setInputError({
      parent: null,
      student: null,
      grade: null
    })
    setStudentConflictDetails({})
    setCourseFormDisabled(true)
    setDisableAddtoCart(true)
    setCurrentBatchInfo({})
    setAreSlotsAvailable(true)
    onHandleModalClose()
  }

  const onSubmitForm = ({ formData }) => {
    setFormData(formData)
  }

  const handleOnFormDataChange = ({ formData }) => {
    if (formRef.current) {
      const { errorSchema } = formRef.current.validate(formData)
      const modifiedErrorSchema = {}

      Object.keys(errorSchema).forEach((key) => {
        if (formData[key]) {
          modifiedErrorSchema[key] = errorSchema[key]
        }
      })

      setErrorSchema(modifiedErrorSchema)
      setFormData(formData)
    }
  }

  const handleBatchInfoChange = async (batchId, property, value) => {
    setBatchInfo((prevState) => {
      const newBatchInfo = {
        ...prevState,
        [batchId]: {
          ...prevState[batchId],
          [property]: value
        }
      }
      if (property === 'startDate') {
        setCurrentBatchInfo({
          batch: batchDetails?.uuid,
          startDate: value
        })
      }

      // Check if both start date and payment cycle are filled
      const currentBatch = newBatchInfo[batchId]
      // Charmi cart-test (2026-05-26) — Custom ₹ (value-mode) discounts
      // store the entered amount on `.value` and leave `.percentage` null.
      // The previous gate only inspected `.percentage > 0`, so picking
      // Custom ₹ + a valid amount kept Add To Cart disabled. Accept either
      // a positive percentage OR a positive value.
      const discount = currentBatch?.additionalDiscount
      const isAdditionalDiscount = currentBatch?.additionalDiscountOffered
        ? (discount?.percentage > 0 || discount?.value > 0)
        : true
      const isFormValid =
        !isEmpty(currentBatch?.startDate) &&
        !isEmpty(currentBatch?.paymentCycle) &&
        !isEmpty(batchDetails) &&
        isAdditionalDiscount

      setCourseFormValid(isFormValid)

      return newBatchInfo
    })
  }

  return (
    <ModalForm
      isModalOpen={isModalOpen}
      enableSave={false}
      showCloseIcon
      onModalClose={onModalClose}
    >
      <Styles.Container>
        <Styles.FormContainer>
          <ParentChildForm
            ref={formRef}
            bookingType={bookingType}
            schema={demoSchema}
            onSubmit={onSubmitForm}
            formData={formData}
            onChangeForm={handleOnFormDataChange}
            errorSchema={errorSchema}
            selectedParentDetails={selectedParentDetails}
            selectedStudentDetails={selectedStudentDetails}
            handleParentSelection={handleParentSelection}
            handleStudentSelection={handleStudentSelection}
            handleBatchSelection={handleBatchSelection}
            students={students}
            inputError={inputError}
            isParentLocked={isParentLocked}
            studentConflictDetails={studentConflictDetails}
            availableBatches={availableBatches}
            batchDetails={batchDetails}
          />
          <SeparationContainer $css={`flex:0.05;`}>
            <Divider orientation="vertical" />
          </SeparationContainer>
          <Styles.ItemsContainer>
            <CourseDetail
              errorSchema={inputError}
              selectedProgram={selectedProgram}
              bookingType={bookingType}
              batchInfo={batchInfo}
              batchDetails={batchDetails}
              selectedBatchType={selectedBatchType}
              courseFormDisabled={courseFormDisabled}
              startDateDisabled={startDateDisabled}
              onBatchInfoChange={handleBatchInfoChange}
            />
          </Styles.ItemsContainer>
        </Styles.FormContainer>
      </Styles.Container>
      <Snackbar
        open={statusMessage.open}
        autoHideDuration={6000}
        onClose={handleCloseMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseMessage} severity={statusMessage.severity}>
          {statusMessage.message}
        </Alert>
      </Snackbar>
      <Styles.ActionsContainer>
        <Actions
          onAddToCartClick={onAddToCartClick}
          disableAddToCart={disableAddToCart}
        />
      </Styles.ActionsContainer>
    </ModalForm>
  )
}

export default AddProgramItemToCartModal
